/**
 * @file `AdminFleetLifecycle` — read-only fleet-lifecycle observer driver.
 *       Wraps one `AdminFleetLifecycleSub` subscription and exposes the
 *       minimal `onFrame` / `close` surface the panel's freshness hook
 *       consumes.
 * @module @labcraft/admin/src/client/fleet
 *
 * The admin console subscribes to the hypervisor's fleet-lifecycle stream
 * (umbrella #1258 Part H / #1340) purely to keep the Hypervisors panel
 * fresh: each frame (id + new state + reason) drives a machine-list cache
 * invalidation so rows + action affordances track reality without
 * reconcile-polling. Receive-only — the operator observes, never drives.
 * Constructed once per panel mount by the `Hypervisor` namespacer's
 * `subscribeFleetLifecycle()`; `close()` tears the subscription down on
 * unmount.
 *
 * Extends `CoreClass` — not a lifecycle sibling: it is a receive-only WS
 * observer constructed within one caller's scope, so it wants the
 * observability surface (`this.debug` / `this.warn`) without the
 * configure/start/stop state machine. Same base + shape as the lab
 * `Terminal` client; standalone (not adopted), mirroring that precedent.
 */

import { CoreClass, CoreError } from '@labcraft/core'
import type { TypedSubscription, Unsubscribe } from '@labcraft/core/frontend'
import type { WsTransport } from './index.ts'
import {
  AdminFleetLifecycleSub,
  type FleetLifecycleClientFrame,
  type FleetLifecycleFrame,
} from '../shared/fleet_subscription.ts'

/** The concrete `TypedSubscription` this driver wraps. */
type TypedSub = TypedSubscription<
  FleetLifecycleClientFrame,
  FleetLifecycleFrame
>

/** Handler for one fleet-lifecycle frame. */
type FrameHandler = (frame: FleetLifecycleFrame) => void

/**
 * Read-only observer over the fleet-lifecycle stream. Satisfies the
 * panel's freshness hook (`onFrame` + `close`) without a `send` surface —
 * the operator observes only.
 */
export class AdminFleetLifecycle extends CoreClass {
  private readonly ws: WsTransport
  private sub: TypedSub | null = null
  private readonly subUnsubs: Unsubscribe[] = []
  private readonly frameHandlers = new Set<FrameHandler>()

  constructor(ws: WsTransport) {
    super({ id: 'fleet' })
    this.ws = ws
  }

  /**
   * Open the subscription to `/api/hypervisor/lifecycle` and route every
   * inbound frame into the local handler set. The client side of the
   * contract is forward-reserved, so nothing is ever sent.
   */
  attach(): void {
    this.debug('attach')
    const sub = this.ws.subscribe(AdminFleetLifecycleSub, { params: {} })
    this.sub = sub
    this.subUnsubs.push(
      sub.onData(frame => {
        for (const h of this.frameHandlers) {
          try {
            h(frame)
          } catch (err) {
            this.warn('onFrame', CoreError.from(err))
          }
        }
      }),
    )
  }

  /** Subscribe to fleet-lifecycle frames. */
  onFrame(handler: FrameHandler): Unsubscribe {
    this.frameHandlers.add(handler)
    return () => this.frameHandlers.delete(handler)
  }

  /** Tear down the subscription and drop every handler. */
  close(): void {
    this.debug('close')
    for (const off of this.subUnsubs) off()
    this.subUnsubs.length = 0
    this.frameHandlers.clear()
    this.sub?.unsubscribe()
    this.sub = null
  }
}
